/**
 * Ilustraciones de la portada, tal como vienen en el demo aprobado
 * (tarjeta #13). Van en línea y no como <img> porque se pintan con los
 * colores de la página (var(--blue), var(--surface)…) y así cambian solas
 * con el modo oscuro.
 */

/**
 * Símbolos que reutilizan las demás ilustraciones: la esfera del isotipo y la
 * persona. Se monta una sola vez por página.
 */
export const ArtworkDefs = () => (
  <svg
    width="0"
    height="0"
    style={{ position: "absolute" }}
    aria-hidden="true"
    focusable="false"
  >
    <defs>
      <radialGradient id="lp-sphere" cx="35%" cy="30%" r="75%">
        <stop offset="0" stopColor="#8FB0EE" />
        <stop offset=".55" stopColor="#4977CC" />
        <stop offset="1" stopColor="#2F57A8" />
      </radialGradient>
      <symbol id="lp-iso" viewBox="0 0 100 100">
        <circle cx="50" cy="48" r="40" fill="url(#lp-sphere)" />
        <ellipse
          cx="36"
          cy="30"
          rx="14"
          ry="8"
          fill="#fff"
          opacity=".28"
          transform="rotate(-30 36 30)"
        />
      </symbol>
      <symbol id="lp-person" viewBox="0 0 120 140">
        <path
          style={{ opacity: "var(--long,0)" }}
          fill="var(--hair,#2B2140)"
          d="M22 66C16 30 38 12 60 12s44 18 38 54l4 40c-12-4-20-12-22-22H40c-2 10-10 18-22 22z"
        />
        <path
          fill="var(--shirt,#4977CC)"
          d="M6 140c0-34 24-48 54-48s54 14 54 48z"
        />
        <path fill="#fff" opacity=".28" d="M46 96l14 18 14-18z" />
        <rect
          x="50"
          y="76"
          width="20"
          height="22"
          rx="9"
          fill="var(--skin,#F3C7A5)"
        />
        <circle cx="60" cy="56" r="29" fill="var(--skin,#F3C7A5)" />
        <path
          fill="var(--hair,#2B2140)"
          d="M31 54C28 28 46 17 62 17c18 0 31 12 28 37-6-12-16-19-30-19S37 42 31 54z"
        />
        <g className="lp-blink">
          <circle cx="49" cy="58" r="3.2" fill="#1B2A41" />
          <circle cx="71" cy="58" r="3.2" fill="#1B2A41" />
        </g>
        <path
          d="M51 70q9 8 18 0"
          fill="none"
          stroke="#1B2A41"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <circle cx="43" cy="68" r="5" fill="#F58E78" opacity=".45" />
        <circle cx="77" cy="68" r="5" fill="#F58E78" opacity=".45" />
        <g
          style={{ opacity: "var(--gl,0)" }}
          fill="none"
          stroke="#1B2A41"
          strokeWidth="2.6"
        >
          <circle cx="49" cy="58" r="9" />
          <circle cx="71" cy="58" r="9" />
          <path d="M58 57h4" />
        </g>
        <g
          style={{ opacity: "var(--hs,0)" }}
          fill="none"
          stroke="#1B2A41"
          strokeLinecap="round"
        >
          <path d="M28 56C26 24 44 14 60 14s34 10 32 42" strokeWidth="5" />
          <rect
            x="21"
            y="50"
            width="12"
            height="22"
            rx="6"
            fill="#fff"
            strokeWidth="4"
          />
          <rect
            x="87"
            y="50"
            width="12"
            height="22"
            rx="6"
            fill="#fff"
            strokeWidth="4"
          />
          <path d="M93 70c0 16-12 20-26 20" strokeWidth="4" />
          <circle cx="64" cy="90" r="5" fill="#1B2A41" />
        </g>
      </symbol>
    </defs>
  </svg>
);

/**
 * Una persona del demo. `look` son sus rasgos: skin, hair y shirt (colores),
 * long (pelo largo), gl (lentes) y hs (diadema), 0 o 1.
 */
export const Person = ({ look }) => {
  const style = Object.fromEntries(
    Object.entries(look).map(([key, value]) => [`--${key}`, value]),
  );
  return (
    <svg viewBox="0 0 120 140" aria-hidden="true">
      <use href="#lp-person" style={style} />
    </svg>
  );
};

/** El isotipo (la esfera azul), suelto. */
export const Iso = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
    <use href="#lp-iso" />
  </svg>
);

export const HeroArt = () => (
  <svg
    viewBox="0 0 480 520"
    role="img"
    aria-label="Ilustración de una factura electrónica timbrada rodeada de mensajes, engrane y folios"
  >
    <circle cx="240" cy="230" r="195" fill="var(--blue-soft)" />
    <circle cx="240" cy="230" r="152" fill="var(--surface)" opacity=".55" />
    <g className="lp-float">
      <rect
        x="130"
        y="62"
        width="230"
        height="300"
        rx="22"
        fill="var(--surface)"
        stroke="var(--navy)"
        strokeWidth="4"
      />
      <rect x="130" y="62" width="230" height="64" rx="22" fill="var(--blue)" />
      <rect x="130" y="100" width="230" height="26" fill="var(--blue)" />
      <text
        x="152"
        y="102"
        fontFamily="Roboto,sans-serif"
        fontWeight="900"
        fontSize="22"
        fill="#fff"
      >
        CFDI
      </text>
      <text
        x="152"
        y="120"
        fontFamily="Roboto Mono,monospace"
        fontSize="11"
        fill="#fff"
        opacity=".85"
      >
        Factura A-0047
      </text>
      <rect
        x="152"
        y="148"
        width="130"
        height="9"
        rx="4.5"
        fill="var(--line)"
      />
      <rect
        x="152"
        y="168"
        width="170"
        height="9"
        rx="4.5"
        fill="var(--line)"
      />
      <rect
        x="152"
        y="188"
        width="100"
        height="9"
        rx="4.5"
        fill="var(--line)"
      />
      <rect
        x="152"
        y="224"
        width="186"
        height="46"
        rx="12"
        fill="var(--yellow)"
      />
      <text
        x="166"
        y="253"
        fontFamily="Roboto,sans-serif"
        fontWeight="700"
        fontSize="13"
        fill="#3A2A00"
      >
        TOTAL
      </text>
      <text
        x="326"
        y="255"
        textAnchor="end"
        fontFamily="Roboto,sans-serif"
        fontWeight="900"
        fontSize="21"
        fill="#3A2A00"
      >
        $3,480
      </text>
      <rect
        x="152"
        y="294"
        width="64"
        height="26"
        rx="13"
        fill="var(--blue-soft)"
      />
      <text
        x="184"
        y="311"
        textAnchor="middle"
        fontFamily="Roboto Mono,monospace"
        fontSize="11"
        fill="var(--blue)"
        fontWeight="500"
      >
        PDF
      </text>
      <rect
        x="224"
        y="294"
        width="64"
        height="26"
        rx="13"
        fill="var(--teal-soft)"
      />
      <text
        x="256"
        y="311"
        textAnchor="middle"
        fontFamily="Roboto Mono,monospace"
        fontSize="11"
        fill="var(--ink-strong)"
        fontWeight="500"
      >
        XML
      </text>
      <g className="lp-stampin">
        <circle cx="322" cy="330" r="30" fill="var(--ok)" />
        <path
          d="M308 331l10 10 18-20"
          fill="none"
          stroke="#fff"
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </g>
    <g className="lp-float d1">
      <rect
        x="22"
        y="78"
        width="132"
        height="76"
        rx="20"
        fill="var(--yellow)"
      />
      <path d="M60 152l-6 26 28-26z" fill="var(--yellow)" />
      <rect x="42" y="98" width="92" height="8" rx="4" fill="#fff" />
      <rect x="42" y="114" width="92" height="8" rx="4" fill="#fff" />
      <rect x="42" y="130" width="58" height="8" rx="4" fill="#fff" />
    </g>
    <g className="lp-float d2">
      <rect x="14" y="238" width="112" height="64" rx="20" fill="var(--teal)" />
      <path d="M96 300l8 22-28-22z" fill="var(--teal)" />
      <rect x="32" y="257" width="76" height="8" rx="4" fill="#fff" />
      <rect x="32" y="272" width="48" height="8" rx="4" fill="#fff" />
    </g>
    <g className="lp-float d3">
      <g className="lp-spin">
        <circle cx="414" cy="108" r="34" fill="var(--coral)" />
        <g fill="var(--coral)">
          <rect x="406" y="60" width="16" height="18" rx="4" />
          <rect x="406" y="138" width="16" height="18" rx="4" />
          <rect x="366" y="100" width="18" height="16" rx="4" />
          <rect x="444" y="100" width="18" height="16" rx="4" />
        </g>
        <g fill="var(--coral)" transform="rotate(45 414 108)">
          <rect x="406" y="60" width="16" height="18" rx="4" />
          <rect x="406" y="138" width="16" height="18" rx="4" />
          <rect x="366" y="100" width="18" height="16" rx="4" />
          <rect x="444" y="100" width="18" height="16" rx="4" />
        </g>
        <circle cx="414" cy="108" r="14" fill="var(--surface)" />
      </g>
    </g>
    <g className="lp-float d1">
      <ellipse cx="240" cy="486" rx="52" ry="13" fill="var(--yellow)" />
      <ellipse
        cx="240"
        cy="472"
        rx="52"
        ry="13"
        fill="var(--yellow)"
        stroke="#E0A93A"
        strokeWidth="2"
      />
      <ellipse
        cx="240"
        cy="458"
        rx="52"
        ry="13"
        fill="var(--yellow)"
        stroke="#E0A93A"
        strokeWidth="2"
      />
      <text
        x="240"
        y="464"
        textAnchor="middle"
        fontFamily="Roboto,sans-serif"
        fontWeight="900"
        fontSize="14"
        fill="#3A2A00"
      >
        folios
      </text>
    </g>
    <g className="lp-float d2">
      <circle cx="446" cy="224" r="24" fill="var(--navy)" />
      <text
        x="446"
        y="230"
        textAnchor="middle"
        fontFamily="Roboto,sans-serif"
        fontWeight="900"
        fontSize="16"
        fill="var(--bg)"
      >
        SAT
      </text>
    </g>
    <g className="lp-float d3">
      <use href="#lp-iso" x="396" y="296" width="70" height="70" />
    </g>
    <path
      className="lp-float"
      d="M62 206l6 13 14 2-10 10 3 14-13-7-13 7 3-14-10-10 14-2z"
      fill="var(--peach)"
    />
  </svg>
);

export const PanelArt = () => (
  <svg
    viewBox="0 0 360 330"
    role="img"
    aria-label="Ilustración de un contador con audífonos frente a su laptop"
  >
    <circle cx="180" cy="170" r="150" fill="var(--surface)" opacity=".7" />
    <g className="lp-float d1">
      <rect
        x="238"
        y="30"
        width="96"
        height="58"
        rx="16"
        fill="var(--yellow)"
      />
      <path d="M262 86l-4 20 20-20z" fill="var(--yellow)" />
      <rect x="254" y="46" width="64" height="7" rx="3.5" fill="#fff" />
      <rect x="254" y="60" width="40" height="7" rx="3.5" fill="#fff" />
    </g>
    <g className="lp-float d2">
      <circle cx="48" cy="84" r="26" fill="var(--coral)" />
      <text
        x="48"
        y="91"
        textAnchor="middle"
        fontFamily="Roboto"
        fontWeight="900"
        fontSize="18"
        fill="#fff"
      >
        32/D
      </text>
    </g>
    <path d="M96 300c0-52 38-82 84-82s84 30 84 82z" fill="var(--blue)" />
    <rect x="164" y="190" width="32" height="34" rx="12" fill="var(--peach)" />
    <circle cx="180" cy="158" r="48" fill="var(--peach)" />
    <path
      d="M132 150c-2-36 22-58 50-58 30 0 52 22 48 58-12-18-30-26-50-26-20 0-36 8-48 26z"
      fill="var(--navy)"
    />
    <path
      d="M128 160c-4-46 24-76 54-76s58 30 52 76"
      fill="none"
      stroke="var(--navy)"
      strokeWidth="9"
      strokeLinecap="round"
    />
    <rect
      x="116"
      y="150"
      width="20"
      height="36"
      rx="9"
      fill="var(--surface)"
      stroke="var(--navy)"
      strokeWidth="4"
    />
    <rect
      x="224"
      y="150"
      width="20"
      height="36"
      rx="9"
      fill="var(--surface)"
      stroke="var(--navy)"
      strokeWidth="4"
    />
    <path
      d="M234 184c0 22-16 30-40 30"
      fill="none"
      stroke="var(--navy)"
      strokeWidth="4"
      strokeLinecap="round"
    />
    <circle cx="192" cy="214" r="6" fill="var(--navy)" />
    <circle cx="164" cy="160" r="4.5" fill="#1B2A41" />
    <circle cx="198" cy="160" r="4.5" fill="#1B2A41" />
    <path
      d="M170 178q10 9 20 0"
      fill="none"
      stroke="#1B2A41"
      strokeWidth="3.5"
      strokeLinecap="round"
    />
    <circle cx="146" cy="176" r="9" fill="#F58E78" opacity=".55" />
    <circle cx="214" cy="176" r="9" fill="#F58E78" opacity=".55" />
    <rect x="84" y="236" width="192" height="12" rx="6" fill="var(--navy)" />
    <rect
      x="104"
      y="262"
      width="152"
      height="52"
      rx="8"
      fill="var(--surface)"
      stroke="var(--navy)"
      strokeWidth="4"
    />
    <rect
      x="116"
      y="274"
      width="62"
      height="7"
      rx="3.5"
      fill="var(--blue-soft)"
    />
    <rect
      x="116"
      y="288"
      width="104"
      height="7"
      rx="3.5"
      fill="var(--blue-soft)"
    />
    <circle cx="232" cy="280" r="10" fill="var(--ok)" />
    <path
      d="M227 280l4 4 7-8"
      fill="none"
      stroke="#fff"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const steps = [
  () => (
    <svg className="lp-ill" viewBox="0 0 150 130" aria-hidden="true">
      <circle cx="75" cy="66" r="58" fill="var(--blue-soft)" />
      <g className="lp-float">
        <rect
          x="48"
          y="14"
          width="54"
          height="100"
          rx="12"
          fill="var(--surface)"
          stroke="var(--navy)"
          strokeWidth="4"
        />
        <circle cx="75" cy="52" r="15" fill="var(--blue)" />
        <path
          d="M70 52l4 4 8-9"
          fill="none"
          stroke="#fff"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect x="60" y="78" width="30" height="7" rx="3.5" fill="var(--line)" />
        <rect
          x="60"
          y="92"
          width="30"
          height="7"
          rx="3.5"
          fill="var(--yellow)"
        />
      </g>
    </svg>
  ),
  () => (
    <svg className="lp-ill" viewBox="0 0 150 130" aria-hidden="true">
      <circle cx="75" cy="66" r="58" fill="var(--teal-soft)" />
      <g className="lp-float d1">
        <rect
          x="34"
          y="18"
          width="82"
          height="96"
          rx="12"
          fill="var(--surface)"
          stroke="var(--navy)"
          strokeWidth="4"
        />
        <rect x="46" y="34" width="58" height="9" rx="4.5" fill="var(--line)" />
        <rect x="46" y="52" width="58" height="9" rx="4.5" fill="var(--line)" />
        <rect x="46" y="70" width="36" height="9" rx="4.5" fill="var(--line)" />
        <rect x="46" y="90" width="58" height="14" rx="7" fill="var(--blue)" />
      </g>
      <path
        d="M100 30l16-16 8 8-16 16-11 3z"
        fill="var(--yellow)"
        stroke="var(--navy)"
        strokeWidth="3"
        strokeLinejoin="round"
      />
    </svg>
  ),
  () => (
    <svg className="lp-ill" viewBox="0 0 150 130" aria-hidden="true">
      <circle cx="75" cy="66" r="58" fill="var(--peach-soft)" />
      <g className="lp-float d2">
        <rect
          x="48"
          y="14"
          width="54"
          height="100"
          rx="12"
          fill="var(--surface)"
          stroke="var(--navy)"
          strokeWidth="4"
        />
        <rect
          x="55"
          y="28"
          width="40"
          height="26"
          rx="8"
          fill="var(--yellow)"
        />
        <rect x="61" y="35" width="26" height="5" rx="2.5" fill="#fff" />
        <rect x="61" y="44" width="16" height="5" rx="2.5" fill="#fff" />
        <circle cx="75" cy="86" r="14" fill="var(--ok)" />
        <path
          d="M69 86l4 4 8-9"
          fill="none"
          stroke="#fff"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <circle cx="112" cy="26" r="11" fill="var(--coral)" />
      <path
        d="M112 20v7M112 31v.5"
        stroke="#fff"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  ),
];

const featureIcons = [
  () => (
    <svg
      aria-hidden="true"
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 3h9l4 4v14H6z" />
      <path d="M15 3v4h4M9 12h6M9 16h6" />
    </svg>
  ),
  () => (
    <svg
      aria-hidden="true"
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" />
      <path d="M8.5 12l2.5 2.5 4.5-5" />
    </svg>
  ),
  () => (
    <svg
      aria-hidden="true"
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
];

/** Ilustración de un paso de "Cómo funciona": 0, 1 o 2. */
export const StepArt = ({ step }) => steps[step]();

/** Ícono de una tarjeta del panel: 0 constancia, 1 32/D, 2 historial. */
export const FeatureIcon = ({ index }) => featureIcons[index]();
